import { PostgresLayerServiceConfigurationDc, ResourceInfoDc } from "@evergis/api";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { dialogResourceSelector } from "../../../../ducks/dialogs/selectors";
import { usePostgresLayerWithTable } from "../../../../hooks/layer/usePostgresLayerWithTable";
import { useLayerPreview } from "../../../../hooks/preview/usePreview";
import { LayerInfo, TableInfo } from "../../../../interfaces/resourceInfo";
import { updateLayer, updateTileLayer } from "../../../../resources/layers";
import { Preview } from "../../../../hooks/useMetaPreview";
import { useResourceUpdate } from "./useResourceUpdate";

export type LayerInfoWithTable = {
  layer: LayerInfo;
  table: TableInfo;
  previewUrl?: string;
  loading?: boolean;
  error?: Error;
  layerUpdated?: boolean;
};

// TODO reduce that
export const useLayerInfoWithUpdate = (): [
  LayerInfoWithTable,
  (configuration: Partial<PostgresLayerServiceConfigurationDc>, preview?: Preview) => Promise<void>,
] => {
  const resourceToEdit = useSelector(dialogResourceSelector);
  const { layer, table, rawConfiguration, loading } = usePostgresLayerWithTable(resourceToEdit);

  const [update, { loading: loadingUpdate, error, data }] = updateLayer.use();
  const [updateTile, { loading: loadingTileUpdate, error: tileError, data: tileData }] = updateTileLayer.use();

  const [previewUrl, updatePreview] = useLayerPreview(layer && layer.name);

  const handleUpdate = useResourceUpdate(update, updatePreview);
  const handleTileUpdate = useResourceUpdate(updateTile, updatePreview);

  const handlePartialUpdate = useCallback(
    (partialUpdate: Partial<PostgresLayerServiceConfigurationDc>, preview?: Preview) => {
      if (layer.type === "PostgresTileLayerService") {
        return handleTileUpdate(
          {
            ...rawConfiguration,
            ...partialUpdate,
            icon: undefined,
            layers: undefined,
          } as ResourceInfoDc,
          preview,
        );
      }

      return handleUpdate(
        {
          ...rawConfiguration,
          ...partialUpdate,
          icon: undefined,
          layers: undefined,
        } as ResourceInfoDc,
        preview,
      );
    },
    [layer.type, handleUpdate, rawConfiguration, handleTileUpdate],
  );

  return [
    {
      layer,
      table,
      previewUrl,
      loading: loading || loadingUpdate || loadingTileUpdate,
      error: layer.type === "PostgresTileLayerService" ? tileError : error,
      layerUpdated: layer.type === "PostgresTileLayerService" ? !!tileData : !!data,
    },
    handlePartialUpdate,
  ];
};
